// The on-demand course's small parts, shared by its pages: the page's head, a status chip, a
// recording's thumbnail, the homework card with its ticks, and Start here's simple text (PCFL v3's
// mdLite: paragraphs, "- " lists and **bold**, drawn as React elements, never as raw HTML).
import { useState } from 'react'
import * as f from '../../lib/format.js'
import { useMember } from '../../app/member.jsx'
import { useViewingAs, readOnlyControl } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { tickHomework } from '../../product/data.js'
import { KindIcon, LockLine, Tick } from '../../product/Icons.jsx'
import { ResourceButton } from '../../product/ResourceButton.jsx'
import { COPY } from './copy.js'
import { ModuleMark } from './Pentagon.jsx'
import { homeworkCount, homeworkGroups, homeworkOf, homeworkTitle, mdBlocks, thumbLabel } from './model.js'

export function PageHead({ kicker, title, lede, id }) {
  return (
    <header className="od-head">
      {kicker && <div className="od-kicker">{kicker}</div>}
      <h1 className="od-h1" id={id}>{title}</h1>
      {lede && <p className="od-lede">{lede}</p>}
    </header>
  )
}

// "Watched", "Carry on from 18:35", "Not watched yet", "Coming soon"
export function Chip({ tone = 'todo', children, big = false }) {
  return (
    <span className={`od-chip od-chip--${tone}${big ? ' od-chip--big' : ''}`}>
      {tone === 'done' && <Tick size={12} stroke={3} />}{children}
    </span>
  )
}

// A recording's thumbnail: Adam's own 16:9 design where the recording has 1 and it's open to them
// (decision 47, 12 Oct: open question 18 answered; row.ready, Adam, 12 Oct: a picture only once the
// recording is open to them), signed through thumbs (product/RecordingThumb.jsx's useRecordingThumbs),
// else the course's own dark green panel with the module's side of the pentagon and its label; then
// its length and a tick once watched, on either. While the picture's link is being signed the panel
// shows empty; a picture that won't load, signed once more, gives the panel back.
// pentagon: whether the course has the 5 Ms (courseModel's pentagon), else the panel has no mark
export function Thumb({ row, index, state = 'todo', small = false, thumbs = null, pentagon = true }) {
  const item = thumbs && row.ready && row.thumbnail_path ? { id: row.id, thumbnailPath: row.thumbnail_path } : null
  const frame = item ? thumbs.frame(item) : null
  const picture = frame?.url || null
  const waiting = Boolean(frame?.pending)
  return (
    <div className={`od-thumb${small ? ' od-thumb--small' : ''}${row.ready ? '' : ' od-thumb--off'}${picture ? ' od-thumb--picture' : ''}`} aria-hidden="true">
      {picture && (
        <img key={picture} className="od-thumb-img" src={picture} alt="" decoding="async"
          onError={e => thumbs.failed(item, e.currentTarget.src)} onLoad={e => thumbs.loaded(item, e.currentTarget.src)} />
      )}
      {!picture && !waiting && pentagon && <span className="od-thumb-mark"><ModuleMark index={index} state={state} size={null} dark /></span>}
      {!picture && !waiting && <span className="od-thumb-label">{thumbLabel(row, row.module)}</span>}
      {row.ready && row.duration > 0 && <span className="od-thumb-dur">{f.clock(row.duration)}</span>}
      {row.ready && row.watched && <span className="od-thumb-seen"><Tick size={small ? 10 : 12} stroke={3.2} /></span>}
    </div>
  )
}

// The homework for 1 module, ticks saved as they go (never while staff view as a member). bare: the
// list alone, as Start here's homework step draws it.
export function Homework({ module, bare = false, headingLevel = 'h2' }) {
  const { data, patch } = useProductData()
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const [line, setLine] = useState(null)
  const items = homeworkOf(data, module.id)
  if (!items.length) return null
  const count = homeworkCount(data, module.id)
  const H = headingLevel

  function toggle(h, done) {
    if (viewingAs) return
    setLine(null)
    patch(d => ({ ...d, ticks: done ? [...new Set([...d.ticks, h.id])] : d.ticks.filter(x => x !== h.id) }))
    tickHomework(me.person.id, h.id, done).catch(e => {
      if (done && e?.code === '23505') return
      patch(d => ({ ...d, ticks: done ? d.ticks.filter(x => x !== h.id) : [...d.ticks, h.id] }))
      setLine(COPY.saveFailed)
    })
  }

  return (
    <div className="od-hw">
      {!bare && (
        <div className="od-hw-head">
          <H className="od-panel-h">{homeworkTitle(data, module)}</H>
          <span className="od-small">{COPY.homeworkOf(count.done, count.total)}</span>
          {count.total > 0 && count.done >= count.total && <Chip tone="done">{COPY.homeworkDone}</Chip>}
        </div>
      )}
      {homeworkGroups(items).map((g, gi) => (
        <div key={gi} className="od-hw-group">
          <h4 className="od-hw-group-h">{g.title}{g.time && <span>{g.time}</span>}</h4>
          {g.items.map(h => {
            const done = data.ticks.includes(h.id)
            return (
              <label key={h.id} className={done ? 'od-check od-check--on' : 'od-check'}>
                <input type="checkbox" checked={done} onChange={e => toggle(h, e.target.checked)} {...readOnlyControl(viewingAs)} />
                <span className="od-check-text">{h.body}</span>
              </label>
            )
          })}
        </div>
      ))}
      <p className="od-small">{COPY.homeworkFoot}</p>
      {line && <p className="od-msg" role="alert">{line}</p>}
    </div>
  )
}

// **bold** inside a line
function inline(text, key) {
  return String(text).split(/(\*\*[^*]+\*\*)/).filter(Boolean).map((bit, i) => (
    /^\*\*[^*]+\*\*$/.test(bit) ? <strong key={`${key}-${i}`}>{bit.slice(2, -2)}</strong> : <span key={`${key}-${i}`}>{bit}</span>
  ))
}

export function Md({ text }) {
  const blocks = mdBlocks(text)
  if (!blocks.length) return null
  return (
    <div className="od-md">
      {blocks.map((b, i) => (b.list
        ? <ul key={i}>{b.list.map((l, j) => <li key={j}>{inline(l, `${i}-${j}`)}</li>)}</ul>
        : <p key={i}>{inline(b.text, i)}</p>))}
    </div>
  )
}

// A module's files as PCFL v3's rows: the kind, the title and its line, and the button (or when it
// opens)
export function FileRows({ cards }) {
  return (
    <div className="od-files">
      {cards.map(c => (
        <div key={c.id} className={c.open ? 'od-file' : 'od-file od-file--locked'}>
          <span className="od-file-icon">{c.open ? <KindIcon kind={c.kind} size={18} /> : <LockLine size={18} />}</span>
          <span className="od-file-text">
            <span className="od-file-title">{c.title}</span>
            {c.meta && <span className="od-file-note">{c.meta}</span>}
          </span>
          {c.open ? <ResourceButton card={c} className="od-btn od-btn--quiet od-btn--sm" /> : <span className="od-file-when">{c.when}</span>}
        </div>
      ))}
    </div>
  )
}
